<template>
  <form
    ref="formRef"
    class="category-feed-search"
    :class="{ '-sticky': isSticky }"
    @submit.prevent="submitForm"
  >
    <div class="grid-2024">
      <div
        class="category-feed-search__row g-col-full g-start-lg-4 g-col-lg-6"
        :class="{ '-search': !isDropdownActive }"
      >
        <div class="category-feed-search__control -input">
          <input
            ref="inputRef"
            v-model="inputValue"
            class="category-feed-search__input"
            type="text"
            :placeholder="t('search_placeholder')"
            @keyup="onInputKeyup"
          />
          <button
            v-if="inputValue.length"
            type="button"
            class="category-feed-search__clear t2"
            @click="clearInput"
          >
            {{ t('clear') }}
          </button>
          <button
            ref="searchBtnRef"
            type="submit"
            class="category-feed-search__btn -magnify"
            @click="toggleSlide"
          />
        </div>
        <div
          class="category-feed-search__control -combobox"
          :class="[{ '-chevron': !isDropdownActive }]"
        >
          <Combobox
            :class-name="['-expertise-category', { '-chevron-visible': !isDropdownActive }]"
            :placeholder="comboboxPlaceholder"
            :filterable="false"
            :items="search.items"
            :show-counter="true"
            :show-clear="false"
            :controls="{
              selected: true,
              unselected: true,
              toggleItemsHandler: toggleAllItems,
            }"
            :on-change="selectItem"
            :on-toggle="toggleOptions"
          />
        </div>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, onBeforeUnmount, useI18n } from '#imports';
  import { Combobox } from '@nuxt-shared/ui/combobox';
  import type { CategoryFeedSearchState } from '../types';

  const props = defineProps<{
    search: CategoryFeedSearchState;
    aliasAll?: string;
  }>();

  const emit = defineEmits<{
    (e: 'update', payload: { value: string; group: string[] }): void;
    (e: 'reload'): void;
  }>();

  const { t } = useI18n({ useScope: 'local' });

  const formRef = ref<HTMLFormElement | null>(null);
  const inputRef = ref<HTMLInputElement | null>(null);
  const searchBtnRef = ref<HTMLButtonElement | null>(null);

  const isSticky = ref(false);
  const isDropdownActive = ref(true);
  const inputValue = ref('');
  const inputValuePrev = ref('');
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let observer: IntersectionObserver | null = null;

  const selectedFilters = computed(
    () =>
      props.search.items?.filter((item) => item.selected).map((item) => item.alias) || [],
  );

  const comboboxPlaceholder = computed(() => {
    if (selectedFilters.value.includes('all') || !selectedFilters.value.length) {
      return t('category_select');
    }
    return selectedFilters.value.join(', ');
  });

  const updateFilters = () => {
    inputValue.value = inputRef.value?.value ?? inputValue.value;
    emit('update', {
      value: inputValue.value,
      group: selectedFilters.value,
    });
  };

  const submitForm = () => {
    updateFilters();
    emit('reload');
    inputValuePrev.value = inputValue.value;
  };

  const onInputKeyup = () => {
    if (!inputValue.value) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (!inputValue.value && inputValuePrev.value) {
          updateFilters();
          emit('reload');
          inputValuePrev.value = inputValue.value;
        }
      }, 2000);
    } else if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };

  const toggleSlide = (e: MouseEvent) => {
    if (window.innerWidth < 576) {
      if (isDropdownActive.value) {
        e.preventDefault();
        isDropdownActive.value = false;
      }
      inputRef.value?.focus();
    }
  };

  const selectItem = () => {
    updateFilters();
    emit('reload');
  };

  const toggleAllItems = (flag: boolean) => {
    for (const item of props.search.items) {
      item.selected = flag;
    }
    updateFilters();
    emit('reload');
  };

  const toggleOptions = (_isComboboxOpen: boolean, e: Event) => {
    if (searchBtnRef.value !== e?.target) {
      isDropdownActive.value = true;
    }
  };

  const clearInput = () => {
    inputRef.value?.focus();
    inputValue.value = '';
  };

  onMounted(() => {
    inputValue.value = props.search.value;
    inputValuePrev.value = inputValue.value;

    if (formRef.value) {
      observer = new IntersectionObserver(
        ([entry]) => {
          isSticky.value = entry.intersectionRatio < 1;
        },
        { threshold: [1] },
      );
      observer.observe(formRef.value);
    }
  });

  onBeforeUnmount(() => {
    if (timeoutId) clearTimeout(timeoutId);
    observer?.disconnect();
  });
</script>

<i18n lang="json" src="../../locale.json" />
